import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, ExternalLink } from "lucide-react";
import SEO, { ORGANISATION_SCHEMA, SITE_URL } from "@/components/SEO";

const HELPFUL_LINKS = [
  {
    title: "Auckland Council — Development Contributions Calculator",
    description: "Estimate DCs payable on additional residential units, before lodging your consent.",
    href: "https://www.aucklandcouncil.govt.nz/development-contributions",
  },
  {
    title: "Auckland Council GIS Maps (GeoMaps)",
    description: "Live planning layers, hazards, overlays, zoning and aerial imagery for any Auckland property.",
    href: "https://geomapspublic.aucklandcouncil.govt.nz/viewer/index.html",
  },
  {
    title: "Watercare — Infrastructure Growth Charge Calculator",
    description: "Estimate the IGC payable on new water and wastewater connections.",
    href: "https://www.watercare.co.nz/builders-developers/infrastructure-growth-charge",
  },
  {
    title: "Auckland Unitary Plan — Operative in Part",
    description: "The full operative Auckland Unitary Plan — zone rules, overlays, definitions and standards.",
    href: "https://unitaryplan.aucklandcouncil.govt.nz/",
  },
  {
    title: "Auckland Design Manual",
    description: "Auckland Council's plain-English 'how to guide' that sits alongside the Unitary Plan — design standards, worked examples and inspiration.",
    href: "https://www.aucklanddesignmanual.co.nz/",
  },
  {
    title: "Quality Planning — The RMA Resource",
    description: "The Ministry for the Environment's RMA practice and guidance hub for planners.",
    href: "https://www.qualityplanning.org.nz/",
  },
  {
    title: "Ministry for the Environment",
    description: "National policy statements, environmental standards and RMA reform updates.",
    href: "https://environment.govt.nz/",
  },
];

const FAQ_SECTIONS = [
  {
    section: "About Code Planning",
    faqs: [
      {
        q: "What makes Code Planning different from larger consultancies?",
        a: "We are a small, senior planning team based on Auckland's North Shore. Every project is run by a senior planner end-to-end — you won't be handed to a junior or moved between staff. We treat every client like our only client, and we cap our workload to make sure every project gets the senior attention it deserves.",
      },
      {
        q: "Are you on Auckland Council's preferred supplier panel — and does it matter?",
        a: "No, we are not on the council's preferred supplier panel. We act exclusively for private clients. This matters because many planning consultancies receive a significant share of their revenue from processing consents on behalf of Auckland Council — which creates a structural conflict of interest when those same firms are then engaged to advocate against the council on private applications. With Code Planning, your interests are the only interests in the room.",
      },
      {
        q: "Do you offer a free initial review?",
        a: "Yes. We offer a Free Property Planning Review for new enquiries — an initial desktop review covering zoning, overlays, hazards, the likely consent pathway, and an indication of cost and timeframe based on the information available. There is no obligation to engage us beyond that.",
      },
      {
        q: "Why shouldn't I just use my architect or surveyor to write my planning application?",
        a: "Same reason you wouldn't ask your builder to do your plumbing or electrical work — different professional disciplines, different insurances, and different expertise. Architects, surveyors and planners are each specialists in their own domain, with their own professional indemnity insurances scoped accordingly. A planner who lives and breathes the consenting process week in and week out will produce a tighter, more defensible application than a competent designer doing it once a year as a sideline — at lower total cost, with fewer Section 92 requests, and with materially less risk of an avoidable notification. We work seamlessly with architects and surveyors as part of the project team — that's the right model.",
      },
      {
        q: "Do you work outside Auckland?",
        a: "The majority of our work is in Auckland, Kaipara and Whangārei, where we have the deepest council relationships. We do, however, prepare consents across the wider Northland region and around New Zealand as opportunities arise. If you have a project elsewhere in the country, get in touch and we'll let you know whether we can take it on directly or refer you to a trusted regional partner.",
      },
    ],
  },
  {
    section: "Resource Consents — The Basics",
    faqs: [
      {
        q: "What is a resource consent?",
        a: "A resource consent is formal permission from your local council under the Resource Management Act 1991. You need one whenever a proposed activity does not comply with a rule in the relevant district, regional or unitary plan — for example exceeding a height limit, building closer to a boundary than the plan allows, subdividing land, or undertaking earthworks on a sloping site. Resource consent answers the question \"can I do this here?\" — separate from building consent, which answers \"can it be built safely and to code?\"",
      },
      {
        q: "How do I know if I need a resource consent?",
        a: "Not every activity needs consent. Many buildings and uses are \"permitted\" under the plan and can proceed without one, provided every standard is met. You will need a resource consent if your proposal breaches any rule in the district or regional plan, or triggers a National Environmental Standard such as the NES for contaminated soil. The Unitary Plan can be difficult to navigate — the simplest way to be sure is a quick desktop review.",
      },
      {
        q: "What's the difference between resource consent, subdivision consent and building consent?",
        a: "Resource consent (Resource Management Act) addresses how your project affects the environment and surrounding properties. Subdivision consent is a particular type of resource consent dealing specifically with new property boundaries and titles. Building consent (Building Act 2004) is a separate approval confirming the construction itself meets the Building Code. Many projects need both resource consent and building consent — typically in that order.",
      },
      {
        q: "What does each \"activity status\" mean?",
        a: "Every activity in a district plan sits in one of six statuses. Permitted: allowed without consent if every standard is met. Controlled: consent is required but must be granted — council can only impose conditions on specific matters. Restricted Discretionary: consent required; council can decline but only on the specified matters. Discretionary: consent required; council can consider any relevant effect. Non-Complying: consent required, and the application must clear two statutory tests before it can be granted. Prohibited: cannot be consented at all under the current plan.",
      },
    ],
  },
  {
    section: "Process & Timing",
    faqs: [
      {
        q: "How long does a resource consent take?",
        a: "A straightforward, non-notified consent typically takes 2–4 weeks for a consultant planner to prepare for lodgement, then 20 working days (about one calendar month) for council to process — though most applications receive at least one Request for Further Information that pauses the clock. Complex projects with several specialists, or that go to a notified or limited-notified process, typically run 6–9 months or more. Note that the period from 20 December to 10 January is statutorily excluded from working day counts, so summer-lodgement applications can lose three weeks immediately.",
      },
      {
        q: "What is an RFI (Request for Further Information)?",
        a: "An RFI is a formal council request for further information about your application, made under Section 92 of the RMA. Council's processing clock stops while you respond. RFIs are common and not necessarily a sign of trouble — a well-prepared application will pre-empt most of them, but expect at least one on almost every consent.",
      },
      {
        q: "Who is considered an \"affected person\"?",
        a: "An affected person is anyone the council determines may be adversely affected by the proposed activity to a degree that is minor or more than minor — typically immediate neighbours. If affected persons provide written approval, they cease to be affected and the application can usually be processed non-notified. Where written approval is not obtained, the application may be limited-notified to those parties so they can submit.",
      },
      {
        q: "What does it mean if my application is \"notified\"?",
        a: "A non-notified consent is decided by council planners only — typically faster, cheaper, and lower risk. A limited-notified consent is advertised to specific identified affected parties who can submit. A publicly notified consent is advertised broadly and any person (other than trade competitors) can submit. Notified consents commonly head to a hearing, can take 6–9+ months, and significantly increase total cost. Securing non-notification is one of the most valuable outcomes a planner can deliver.",
      },
    ],
  },
  {
    section: "Costs",
    faqs: [
      {
        q: "How much does a resource consent cost?",
        a: "Our planner fee for a typical resource consent sits in the range of $3,000 to $8,000 + GST, depending on complexity. A simple non-notified consent on a straightforward site sits at the lower end of that range; consents involving multiple specialists, sensitive overlays, or substantial design work sit higher. Notified consents and hearings are quoted separately. We provide a clear written fee proposal before any work starts — no surprises.",
      },
      {
        q: "What other costs are there beyond the planner's fee?",
        a: "Council application fees, charged separately by Auckland Council on a deposit-plus-actual-cost basis. Specialist reports (engineer, geotech, traffic, ecologist, acoustic, contamination, surveyor) where required — typically scoped at the same time as the planning work. After lodgement, costs can include responding to a Section 92 RFI, an external review by a council-appointed specialist, or a notified process. We flag every likely cost up front so you can budget accordingly.",
      },
      {
        q: "Are council fees fixed?",
        a: "No. Council charges an initial deposit on lodgement and then a top-up calculated on actual processing time at hourly rates. Specialist council teams (urban design, heritage, arborist, Watercare) each charge their own hourly rates that flow through to your final fee. The Auckland Council resource consent fee schedule is published online.",
      },
    ],
  },
  {
    section: "After Consent is Granted",
    faqs: [
      {
        q: "How long does a granted consent last?",
        a: "Most resource consents have a five-year lapsing period — meaning you have five years to \"give effect to\" the consent. Once given effect to, land use and subdivision consents typically have no expiry date. Regional consents (discharge, water, coastal) usually have a stated duration, anywhere from 1 to 35 years.",
      },
      {
        q: "Can I extend a consent that's about to lapse?",
        a: "Yes — you can apply to extend the lapsing period, but you must apply before the consent lapses. Council will consider whether you've made substantial progress towards giving effect to the consent, whether affected parties consent to the extension, and whether the plan has changed materially.",
      },
      {
        q: "Can I change the conditions of a granted consent?",
        a: "Yes. Minor changes may fall within the existing scope. Larger changes require a Section 127 application to vary the conditions. The Section 127 process is similar to a new consent application, except council can only assess the proposed changes — not revisit the merits of the original consent.",
      },
      {
        q: "What is a Code Compliance Certificate (CCC)?",
        a: "A CCC is issued under the Building Act once all building consent requirements have been met and inspections passed. It is separate from any resource consent conditions. Some resource consent conditions (such as completion of landscaping or stormwater works) must also be satisfied before s224(c) on a subdivision, or before final operation of a new use.",
      },
    ],
  },
  {
    section: "Other Useful Terms",
    faqs: [
      {
        q: "What is a Certificate of Compliance?",
        a: "A formal council confirmation that an activity is permitted under the current plan. It's not legally required, but it provides a useful safeguard if you want certainty that future plan changes won't affect a project that is currently as-of-right.",
      },
      {
        q: "What is a National Policy Statement (NPS) or National Environmental Standard (NES)?",
        a: "Both are higher-order planning instruments issued by central government. NPSs set policy direction that councils must give effect to in their plans (e.g. NPS for Urban Development). NESs are operative rules that apply nationally — for example the NES-CS for managing contaminants in soil, which is triggered on any earthworks or subdivision on a site with a history of HAIL activity.",
      },
    ],
  },
];

const FaqItem = ({ faq, index, open, onToggle }) => (
  <div className="border-b border-neutral-800">
    <button
      onClick={() => onToggle(index)}
      className="w-full flex items-center justify-between text-left py-6 group"
      data-testid={`faq-q-${index}`}
    >
      <span className="font-heading text-lg md:text-xl text-white pr-4 group-hover:opacity-80 transition-opacity">
        {faq.q}
      </span>
      <ChevronDown
        className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
          open ? "rotate-180 text-white" : ""
        }`}
      />
    </button>
    {open && (
      <div className="pb-6 text-gray-300 leading-relaxed" data-testid={`faq-a-${index}`}>
        {faq.a}
      </div>
    )}
  </div>
);

export default function FaqsPage() {
  const [openIdx, setOpenIdx] = useState(0);
  const toggle = (i) => setOpenIdx(openIdx === i ? -1 : i);

  const allFaqs = FAQ_SECTIONS.flatMap((s) => s.faqs);

  return (
    <div className="page-wrapper bg-ink" data-testid="faqs-page">
      <SEO
        title="FAQ & Resources — Common Questions and Useful Planning Links"
        description="Plain-English answers to common resource consent questions, plus a curated list of helpful planning links — Auckland Council calculators, GIS, the Unitary Plan, Quality Planning and more."
        path="/faqs"
        keywords="resource consent faq, planning consultant faq auckland, rma faq nz, resource consent cost, auckland council gis, development contribution calculator"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${SITE_URL}/faqs#faq`,
            mainEntity: allFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          ORGANISATION_SCHEMA,
        ]}
      />

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            FAQ & Resources
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Resource Consents,
            <br />
            Answered.
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed mb-12 max-w-2xl">
            Plain-English answers to the questions we hear every week — plus a
            curated list of the calculators, maps and reference documents we use
            every day. If your question isn't here, drop us a line.
          </p>

          {FAQ_SECTIONS.map((section, sIdx) => {
            // global index for each question so only one accordion is open at a time
            const startIdx = FAQ_SECTIONS.slice(0, sIdx).reduce(
              (acc, s) => acc + s.faqs.length,
              0
            );
            return (
              <div key={section.section} className="mb-12">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                  {section.section}
                </h2>
                <div>
                  {section.faqs.map((faq, i) => (
                    <FaqItem
                      key={i}
                      faq={faq}
                      index={startIdx + i}
                      open={openIdx === startIdx + i}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Helpful Links */}
          <div className="mt-16 mb-16" data-testid="helpful-links-section">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
              Helpful Links
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-3">
              Tools, Calculators & References
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl">
              External resources we lean on for desktop reviews, fee estimates
              and rule checks. Bookmark whichever ones are relevant to your
              project.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {HELPFUL_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-neutral-800 p-6 hover:border-white transition-colors duration-300 flex flex-col"
                  data-testid={`resource-link-${link.title.split(" ")[0].toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading text-base md:text-lg text-white leading-snug pr-3">
                      {link.title}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white flex-shrink-0 transition-colors" />
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {link.description}
                  </p>
                </a>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 border border-neutral-800 p-8 md:p-12 bg-neutral-950">
            <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
              Still have a question?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl">
              Every project is different. Request a Free Property Planning
              Review — we'll provide an initial indication of the likely
              consent pathway, cost and timeframe based on the information
              available.
            </p>
            <Link
              to="/contact#property-form"
              className="inline-flex items-center gap-2 text-white border border-white px-6 py-3 hover:bg-white hover:text-ink transition-colors uppercase tracking-widest text-sm"
              data-testid="faqs-cta-contact"
            >
              Check My Property <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
